import React, { useEffect, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 판매·재고 — salesweb(FastAPI) 백엔드 재사용: 누적 데이터 조회 + 이지어드민 수집
const LIMIT = 200

// 카페24 관리자 API — OAuth 연동 + 오늘 매출/주문건수만 표시(고객명·연락처·주소는 조회하지 않음)
function Cafe24Panel() {
  const [status, setStatus] = useState(null)
  const [today, setToday] = useState(null)
  const [err, setErr] = useState('')
  const [form, setForm] = useState({ mall_id: '', client_id: '', client_secret: '' })
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  const load = () => {
    portal.get('/api/cafe24/status').then(setStatus).catch((e) => setErr(e.message))
  }
  useEffect(() => { load() }, [])

  useEffect(() => {
    if (status?.has_token) {
      portal.get('/api/cafe24/today').then(setToday).catch((e) => setErr(e.message))
    }
  }, [status?.has_token])

  const saveConfig = async () => {
    setSaving(true)
    try {
      await portal.post('/api/cafe24/config', form)
      notify('설정 저장 완료')
      load()
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const connect = async () => {
    try {
      const r = await portal.get('/api/cafe24/authorize')
      window.open(r.authorize_url, '_blank', 'noopener')
    } catch (e) { setErr(e.message) }
  }

  if (!status) return null
  const configured = status.has_client_id && status.has_secret && status.mall_id

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <h3>카페24</h3>
      {err && <div className="error-box">{err}</div>}
      {!configured && (
        <div className="row">
          <input placeholder="몰 아이디 (예: ergo2)" style={{ width: 140 }}
            value={form.mall_id} onChange={(e) => setForm({ ...form, mall_id: e.target.value })} />
          <input placeholder="Client ID" style={{ width: 220 }}
            value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} />
          <input placeholder="Client Secret" type="password" style={{ width: 220 }}
            value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} />
          <button className="btn" disabled={saving || !form.mall_id || !form.client_id || !form.client_secret}
            onClick={saveConfig}>저장</button>
        </div>
      )}
      {configured && !status.has_token && (
        <div className="row">
          <span className="muted">몰 {status.mall_id} · 인증정보 등록됨 · 아직 연동 전</span>
          <button className="btn" onClick={connect}>카페24 연동하기</button>
        </div>
      )}
      {configured && status.has_token && (
        <div className="row">
          {today ? (
            <>
              <span>오늘({today.date}) 주문 <b>{fmt(today.order_count)}</b>건</span>
              <span>매출 <b>{fmt(today.sales_amount)}</b>원</span>
            </>
          ) : <span className="muted">오늘 데이터 불러오는 중…</span>}
          <span className="muted" style={{ marginLeft: 'auto' }}>연동됨 · 몰 {status.mall_id}</span>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}

export default function Sales({ currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [kinds, setKinds] = useState(null)
  const [kind, setKind] = useState('')
  const [summary, setSummary] = useState(null)
  const [date, setDate] = useState('')
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [collect, setCollect] = useState({ start: '', end: '' })
  const [job, setJob] = useState(null)
  const [cookieInfo, setCookieInfo] = useState(null)
  const [cookieVal, setCookieVal] = useState('')
  const [toast, setToast] = useState('')

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  useEffect(() => {
    portal.get('/api/kinds')
      .then((k) => { setKinds(k); const first = Object.keys(k)[0]; setKind((p) => p || first) })
      .catch((e) => setErr(e.message))
    portal.get('/api/settings/cookie').then(setCookieInfo).catch(() => {})
  }, [])

  useEffect(() => {
    if (!kind) return
    setSummary(null); setDate(''); setOffset(0)
    portal.get(`/api/summary/${kind}`).then(setSummary).catch((e) => setErr(e.message))
  }, [kind])

  useEffect(() => {
    if (!kind) return
    setData(null)
    portal.get(`/api/data/${kind}?date=${date}&limit=${LIMIT}&offset=${offset}`)
      .then(setData).catch((e) => setErr(e.message))
  }, [kind, date, offset])

  // 수집 작업 폴링
  useEffect(() => {
    if (!job?.id || ['done', 'error', 'finished'].includes(job.status)) return
    const t = setTimeout(() => {
      portal.get(`/api/collect/${job.id}`).then((j) => setJob({ ...j, id: job.id })).catch(() => {})
    }, 2000)
    return () => clearTimeout(t)
  }, [job])

  const runCollect = async () => {
    try {
      const r = await portal.post('/api/collect', collect)
      setJob({ ...(r || {}), id: r.job_id ?? r.id, status: r.status ?? 'running' })
      notify('수집 시작')
    } catch (e) { setErr(e.message) }
  }

  const saveCookie = async () => {
    try {
      const r = await portal.post('/api/settings/cookie', { cookie: cookieVal })
      setCookieInfo({ has: r.length > 0, length: r.length })
      setCookieVal(''); notify('쿠키 저장 완료')
    } catch (e) { setErr(e.message) }
  }

  if (err && !kinds) return <><h2>판매·재고</h2><div className="error-box">{err}</div></>
  if (!kinds) return <div className="muted">불러오는 중…</div>

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>판매·재고 누적 데이터</h2>
        <NoteDrawer target="sales" screenKey="sales_inventory"
          context={{ kind, date }} contextLabel={`${kinds?.[kind]?.label || kind}${date ? ' · ' + date : ''}`}
          onNavigate={(ctx) => { if (ctx?.kind) setKind(ctx.kind); if (ctx?.date !== undefined) setDate(ctx.date) }}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      <Cafe24Panel />
      {err && <div className="error-box">{err}</div>}
      <div className="row" style={{ marginBottom: 12 }}>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(kinds).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={date} onChange={(e) => { setDate(e.target.value); setOffset(0) }}>
          <option value="">전체 날짜</option>
          {summary?.dates?.map((d) => (
            <option key={d['기준일자']} value={d['기준일자']}>{d['기준일자']} ({fmt(d['건수'])}건)</option>
          ))}
        </select>
        {summary && <span className="muted">누적 {fmt(summary.total)}건</span>}
        <a className="btn secondary" style={{ textDecoration: 'none' }}
          href={`/api/export/${kind}?date=${date}`} target="_blank" rel="noreferrer">엑셀 다운로드</a>
      </div>

      <div className="panel">
        <h3>이지어드민 수집</h3>
        <div className="row">
          <input type="date" value={collect.start} onChange={(e) => setCollect({ ...collect, start: e.target.value })} />
          <span className="muted">~</span>
          <input type="date" value={collect.end} onChange={(e) => setCollect({ ...collect, end: e.target.value })} />
          <button className="btn" onClick={runCollect} disabled={!collect.start}>수집 실행</button>
          {job && <span className={job.status === 'error' ? 'warn' : 'muted'}>
            상태: {job.status}{job.message ? ` · ${job.message}` : ''}{job.progress ? ` · ${job.progress}` : ''}
          </span>}
          <span className="muted" style={{ marginLeft: 'auto' }}>
            이지어드민 쿠키 {cookieInfo?.has ? `등록됨(${cookieInfo.length}자)` : '없음'}
          </span>
          <input placeholder="새 쿠키 붙여넣기" style={{ width: 200 }} value={cookieVal}
            onChange={(e) => setCookieVal(e.target.value)} />
          <button className="btn secondary" onClick={saveCookie} disabled={!cookieVal.trim()}>쿠키 저장</button>
        </div>
      </div>

      <div className="panel">
        {!data && <div className="muted">데이터 불러오는 중…</div>}
        {data && (
          <>
            <table>
              <thead>
                <tr>{data.columns.map((c) => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={data.columns.length || 1} className="c muted">데이터가 없습니다</td></tr>}
                {data.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((v, j) => (
                      <td key={j} className={typeof v === 'number' ? '' : 'l'}>
                        {typeof v === 'number' ? fmt(v, 2) : String(v ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>이전</button>
              <span className="muted">{fmt(offset + 1)}–{fmt(Math.min(offset + LIMIT, data.total))} / {fmt(data.total)}</span>
              <button className="btn secondary" disabled={offset + LIMIT >= data.total} onClick={() => setOffset(offset + LIMIT)}>다음</button>
            </div>
          </>
        )}
      </div>
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
